import { useState } from 'react'
import RoleSelector from './RoleSelector'

interface InviteModalProps {
  onClose: () => void
  onInvite: (email: string, role: string) => Promise<void>
  isLoading?: boolean
}

export default function InviteModal({ onClose, onInvite, isLoading = false }: InviteModalProps) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('tenant_admin')
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!email) {
      setError('Email is required')
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    try {
      await onInvite(email, role)
      onClose()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string }
      setError(error.response?.data?.detail || error.message || 'Failed to send invitation')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="card p-6 w-full max-w-md mx-4">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Invite Team Member</h2>
        <p className="text-gray-600 text-sm mb-6">
          Send an invitation email to add a new member to your team.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="colleague@company.com"
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="label">Role</label>
            <RoleSelector value={role} onChange={setRole} disabled={isLoading} />
            <p className="text-xs text-gray-500 mt-1">
              {role === 'tenant_admin'
                ? 'Admins can manage team members, API keys, and all settings.'
                : 'Members can view and use profiles but cannot manage settings.'}
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="btn btn-primary">
              {isLoading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
